import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, Heart, Activity, Brain, Shield, Users } from 'lucide-react';
import { HealthScore } from '@/types/healthScore';

interface ScorePillar {
  name: string;
  score: number;
  maxScore: number;
  trend: 'up' | 'down' | 'stable';
  icon: React.ReactNode;
  description: string;
  recommendation?: string;
}

interface ScoreDetailsDialogProps {
  memberName: string;
  totalScore: number;
  baselineScore?: number;
  actionableScore?: number;
  healthScore: HealthScore | null;
  children: React.ReactNode;
}

export const ScoreDetailsDialog = ({ 
  memberName, 
  totalScore, 
  baselineScore = 180,
  actionableScore = 562,
  healthScore,
  children 
}: ScoreDetailsDialogProps) => {
  const [open, setOpen] = useState(false);

  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 85) return '#22C55E'; // green-500
    if (percentage >= 70) return '#84CC16'; // lime-500
    if (percentage >= 55) return '#EAB308'; // yellow-500
    if (percentage >= 40) return '#F97316'; // orange-500
    return '#EF4444'; // red-500
  };

  const getScoreGrade = (score: number) => {
    if (score >= 850) return 'A - Excellent';
    if (score >= 700) return 'B - Good';
    if (score >= 550) return 'C - Fair';
    if (score >= 400) return 'D - Needs Attention';
    return 'E - Poor';
  };

  const getTrafficLight = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 70) return '🟢';
    if (percentage >= 40) return '🟡';
    return '🔴';
  };

  const scorePillars: ScorePillar[] = [
    {
      name: 'Cardiometabolic',
      score: healthScore?.cardio ?? 0,
      maxScore: 150,
      trend: 'up',
      icon: <Heart className="h-5 w-5" />,
      description: 'Blood pressure, lipids, HbA1c, BMI/waist',
      recommendation: '+8 pts if you maintain BP <120/80 for 4 weeks'
    },
    {
      name: 'Lifestyle',
      score: healthScore?.lifestyle ?? 0,
      maxScore: 150,
      trend: 'up',
      icon: <Activity className="h-5 w-5" />,
      description: 'Physical activity, diet quality, sleep consistency',
      recommendation: '+12 pts if you walk ≥7k steps avg for 4 weeks'
    },
    {
      name: 'Mental & Social',
      score: healthScore?.mental ?? 0,
      maxScore: 150,
      trend: 'stable',
      icon: <Brain className="h-5 w-5" />,
      description: 'PHQ-9/GAD-7 trends, social engagement, caregiver burden',
      recommendation: '+10 pts if you complete weekly mood check-ins'
    },
    {
      name: 'Preventive Care',
      score: healthScore?.preventive ?? 0,
      maxScore: 150,
      trend: 'down',
      icon: <Shield className="h-5 w-5" />,
      description: 'Immunizations, cancer screening, dental/vision',
      recommendation: '+15 pts if you schedule overdue screenings'
    },
    {
      name: 'Engagement & Adherence',
      score: healthScore?.engagement ?? 0,
      maxScore: 150,
      trend: 'up',
      icon: <Users className="h-5 w-5" />,
      description: 'Medication adherence, follow-up visits, data sync',
      recommendation: '+5 pts if you sync data daily for 2 weeks'
    }
  ];

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Health Score Details - {memberName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Overall Score */}
          <Card className="p-6 text-center">
            <div className="space-y-2">
              <div className="text-4xl font-bold" style={{ color: getScoreColor(totalScore, 1000) }}>
                {totalScore}/1000
              </div>
              <Badge 
                variant="secondary"
                className="text-lg px-3 py-1"
                style={{ 
                  backgroundColor: getScoreColor(totalScore, 1000) + '20',
                  color: getScoreColor(totalScore, 1000)
                }}
              >
                {getScoreGrade(totalScore)}
              </Badge>
            </div>
          </Card>

          {/* Baseline vs Actionable Breakdown */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-semibold text-blue-600 mb-2">Baseline Band</h4>
              <div className="text-2xl font-bold text-blue-600">{baselineScore}/250</div>
              <p className="text-sm text-muted-foreground mt-1">
                Genetics, demographics, early-life factors
              </p>
            </Card>
            <Card className="p-4">
              <h4 className="font-semibold text-primary mb-2">Actionable Band</h4>
              <div className="text-2xl font-bold text-primary">{actionableScore}/750</div>
              <p className="text-sm text-muted-foreground mt-1">
                Lifestyle and health behaviors you can control
              </p>
            </Card>
          </div>

          {/* Five Pillars */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Actionable Health Pillars</h3>
            {scorePillars.map((pillar, index) => {
              const percentage = (pillar.score / pillar.maxScore) * 100;
              return (
                <Card key={index} className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getTrafficLight(pillar.score, pillar.maxScore)}</span>
                          <div 
                            className="p-2 rounded-full"
                            style={{ 
                              backgroundColor: getScoreColor(pillar.score, pillar.maxScore) + '20',
                              color: getScoreColor(pillar.score, pillar.maxScore)
                            }}
                          >
                            {pillar.icon}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium">{pillar.name}</h4>
                          <p className="text-sm text-muted-foreground">{pillar.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {pillar.trend && getTrendIcon(pillar.trend)}
                        <div className="text-right">
                          <div className="font-semibold">
                            {pillar.score}/{pillar.maxScore}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {Math.round(percentage)}%
                          </div>
                        </div>
                      </div>
                    </div>
                    <Progress 
                      value={percentage} 
                      className="h-2"
                    />
                    {pillar.recommendation && (
                      <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                        💡 {pillar.recommendation}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Key Insights */}
          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-3">Key Insights</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                <p>Strong engagement and cardiometabolic health are your top strengths</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                <p>Preventive care needs attention - schedule overdue screenings for +15 points</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                <p>Your baseline score reflects favorable genetic and demographic factors</p>
              </div>
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
